import { Empty, Select, Table, Tag } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { dashboardService } from '@/services'
import { useRegionStore } from '@/stores/regionStore'
import { formatDateTime } from '@/utils/cn'
import type { CurrentStateRow } from '@/types'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { PageError, PageLoading } from '@/components/common/PageStates'

type TagKind = 'all' | 'scannable' | 'non_scan'

const TAG_KIND_OPTIONS: { value: TagKind; label: string }[] = [
  { value: 'all', label: 'All tags & line items' },
  { value: 'scannable', label: 'Scannable RFID tags' },
  { value: 'non_scan', label: 'Non-scannable line items' },
]

function partyLabel(code: string | null, name: string | null) {
  if (!code && !name) return '—'
  if (code && name?.trim()) return `${name} (${code})`
  return code || name || '—'
}

export default function CurrentStatePage() {
  const regionView = useRegionStore((s) => s.view)
  const [tagKind, setTagKind] = useState<TagKind>('all')

  const q = useQuery({
    queryKey: ['current-state', regionView, tagKind],
    queryFn: () => dashboardService.getCurrentState({ tag_kind: tagKind }),
  })

  const rows = q.data?.data ?? []
  const grouped = q.data?.grouped
  const bySender = grouped?.bySender ?? grouped?.byCustomer ?? []
  const byReceiver = grouped?.byReceiver ?? grouped?.byWarehouse ?? []

  return (
    <PageContainer fluid>
      <PageHeader
        title="Current State"
        subtitle="Latest known snapshot per RFID tag or line item (up to 500 rows)."
      />

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Select
          className="w-56"
          value={tagKind}
          options={TAG_KIND_OPTIONS}
          onChange={(v: TagKind) => setTagKind(v)}
        />
      </div>

      {q.isError ? (
        <PageError message={(q.error as { message?: string })?.message} />
      ) : q.isLoading && !q.data ? (
        <PageLoading />
      ) : (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            <SectionCard title="By sender" variant="table">
              {bySender.length === 0 ? (
                <div className="p-6">
                  <Empty description="No sender data" />
                </div>
              ) : (
                <Table
                  className="leap-table"
                  size="small"
                  pagination={false}
                  rowKey="location"
                  dataSource={bySender}
                  columns={[
                    { title: 'Sender', dataIndex: 'location' },
                    { title: 'Tags', dataIndex: 'count', width: 90 },
                  ]}
                />
              )}
            </SectionCard>
            <SectionCard title="By receiver" variant="table">
              {byReceiver.length === 0 ? (
                <div className="p-6">
                  <Empty description="No receiver data" />
                </div>
              ) : (
                <Table
                  className="leap-table"
                  size="small"
                  pagination={false}
                  rowKey="location"
                  dataSource={byReceiver}
                  columns={[
                    { title: 'Receiver', dataIndex: 'location' },
                    { title: 'Tags', dataIndex: 'count', width: 90 },
                  ]}
                />
              )}
            </SectionCard>
          </div>

          <SectionCard title="Tag snapshot" count={rows.length} variant="table">
            <Table<CurrentStateRow>
              className="leap-table"
              rowKey={(r) => `${r.region ?? 'x'}-${r.rfid_code}`}
              size="middle"
              scroll={{ x: 1100 }}
              loading={q.isFetching}
              dataSource={rows}
              columns={[
                ...(rows.some((r) => r.region)
                  ? [
                      {
                        title: 'Region',
                        dataIndex: 'region' as const,
                        width: 72,
                        render: (v: string) => v || '—',
                      },
                    ]
                  : []),
                {
                  title: 'RFID',
                  dataIndex: 'rfid_code',
                  render: (v: string) => (
                    <Link className="leap-link" to={`/tag-journey?code=${encodeURIComponent(v)}`}>
                      {v}
                    </Link>
                  ),
                },
                { title: 'Type', dataIndex: 'transfer_type', width: 120 },
                {
                  title: 'In Transit',
                  dataIndex: 'in_transit',
                  width: 100,
                  render: (v?: string | null) =>
                    v === 'X' || v === 'x' ? <Tag color="orange">X</Tag> : '—',
                },
                {
                  title: 'Sender',
                  key: 'sender',
                  render: (_: unknown, row: CurrentStateRow) =>
                    partyLabel(row.sender_code, row.sender_name),
                },
                {
                  title: 'Receiver',
                  key: 'receiver',
                  render: (_: unknown, row: CurrentStateRow) =>
                    partyLabel(row.receiver_code, row.receiver_name),
                },
                {
                  title: 'Document',
                  dataIndex: 'last_document',
                  render: (v: string, row) => {
                    const inTransit = row.in_transit === 'X' || row.in_transit === 'x'
                    const doc = inTransit ? '' : String(v ?? '').trim()
                    if (!doc) return '—'
                    return (
                      <Link
                        className="leap-link"
                        to={`/movement-document?document=${encodeURIComponent(doc)}`}
                      >
                        {doc}
                      </Link>
                    )
                  },
                },
                {
                  title: 'Updated',
                  dataIndex: 'updated_at',
                  width: 160,
                  render: (v: string) => formatDateTime(v),
                },
              ]}
              pagination={{ pageSize: 25 }}
            />
          </SectionCard>
        </>
      )}
    </PageContainer>
  )
}
